// Connexions (lot 4.1) : tout ce qu'Alfred lit chez d'autres services, avec un état honnête. Un incident passager
// ne se voit pas ; un mot de passe refusé se voit, avec ce qu'il faut faire et un champ pour le remplacer. C'est ici
// que s'ajoutent l'agenda iCloud, les agendas par adresse (lot 4.4), les boîtes mail (lot 4.2) et les dossiers de documents (lot 4.5).

import { type FormEvent, useCallback, useEffect, useState } from 'react';

import type { Actions } from '../App';
import { EnTeteRetour, Fenetre } from '../components';
import { type Connexion, type FournisseurMail, type MailReleve, NonConnecte, api } from '../lib/api';

const ETATS: Record<Connexion['etat'], string> = { ok: 'connecté', a_reconnecter: 'à reconnecter', erreur: 'en erreur' };

function depuis(iso: string | null): string {
  if (!iso) return 'jamais synchronisé';
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 1) return 'synchronisé à l’instant';
  if (min < 60) return `synchronisé il y a ${min} min`;
  return `synchronisé le ${new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`;
}

function Ligne({ c, faire }: { c: Connexion; faire: (f: () => Promise<unknown>, succes: string) => Promise<boolean> }) {
  const [secret, setSecret] = useState<string | null>(c.etat === 'a_reconnecter' ? '' : null);
  const reconnecter = async (e: FormEvent) => {
    e.preventDefault();
    if (secret && await faire(() => api.reconnecterConnexion(c.id, secret.trim()), `${c.nom} : reconnecté.`)) setSecret(null);
  };
  return (
    <div className="connexion">
      <div className="t">
        <span>{c.nom}</span>
        <small className={c.etat === 'ok' ? 'muted' : 'attention'}>
          {ETATS[c.etat]}{c.geree ? ` · ${depuis(c.derniereSynchroLe)}` : ''}
          {(c.etat === 'erreur' || c.fournisseur === 'dossier') && c.erreur ? ` · ${c.erreur}` : ''}
        </small>
        {c.calendriers.length > 0 && <small className="muted">{c.calendriers.join(', ')}</small>}
        {c.fournisseur === 'ics' && <small className="muted">{c.hote}{c.confiance ? ' · de confiance' : ' · externe'}</small>}
        {c.fournisseur === 'dossier' && <small className="muted">/{c.chemin === '/' ? '' : c.chemin}</small>}
      </div>
      {c.conseil && <p className="bandeau-erreur" style={{ margin: 0 }}>{c.conseil}</p>}
      {secret !== null && (
        <form className="formulaire" onSubmit={(e) => void reconnecter(e)}>
          <input className="saisie texte" placeholder={c.fournisseur === 'ics' ? 'Nouvelle adresse de l’agenda' : 'Nouveau mot de passe d’application'} autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)} aria-label="Nouveau mot de passe d’application" />
          <div className="boutons">
            <button className="btn principal" disabled={!secret.trim()}>Reconnecter</button>
            {c.etat !== 'a_reconnecter' && <button className="btn" type="button" onClick={() => setSecret(null)}>Annuler</button>}
          </div>
        </form>
      )}
      {c.fournisseur === 'ics' && secret === null && (
        <label className="entretien-case">
          <input type="checkbox" checked={c.confiance === true} onChange={(e) => void faire(() => api.reglerConfianceConnexion(c.id, e.target.checked), e.target.checked ? `${c.nom} : de confiance.` : `${c.nom} : externe.`)} />
          <span>De confiance<br /><small className="muted">Cochez si vous seul y écrivez. Sinon, ce qu’Alfred voudra écrire après l’avoir lu attendra votre validation.</small></span>
        </label>
      )}
      {c.geree && secret === null && (
        <div className="boutons">
          <button className="btn petit" onClick={() => void faire(() => api.synchroniserConnexion(c.id), `${c.nom} : à jour.`)}>Synchroniser</button>
          <button className="btn petit" onClick={() => setSecret('')} hidden={c.fournisseur === 'dossier'}>{c.fournisseur === 'ics' ? 'Changer l’adresse' : 'Changer le mot de passe'}</button>
          {c.fournisseur === 'dossier' && c.erreur?.includes('confirmez') && (
            <button className="btn petit" onClick={() => void faire(() => api.confirmerDisparitions(c.id), 'Index mis à jour.')}>Confirmer les suppressions</button>
          )}
          <button className="btn petit" onClick={() => void faire(() => api.supprimerConnexion(c.id), `${c.nom} : retiré.`)}>Retirer</button>
        </div>
      )}
    </div>
  );
}

/** Où activer le transfert automatique, selon la boîte principale. */
const TRANSFERTS: [string, string[]][] = [
  ['Gmail', [
    'Gmail sur ordinateur → roue dentée → « Voir tous les paramètres » → onglet « Transfert et POP/IMAP ».',
    '« Ajouter une adresse de transfert » : l’adresse dédiée. Gmail y envoie un code : ouvrez la boîte dédiée (sur le site de GMX ou de Gmail) pour le recopier.',
    'Cochez « Transférer une copie des messages entrants » et gardez « conserver la copie dans la boîte de réception ».',
    'Pour ne transférer qu’une partie : créez plutôt un filtre (« Créer un filtre » → « Transférer à »).',
  ]],
  ['iCloud', [
    'icloud.com/mail sur ordinateur → roue dentée → « Réglages » → « Transfert ».',
    'Cochez « Transférer mes e-mails à » et indiquez l’adresse dédiée. Ne cochez pas la suppression des messages après transfert.',
  ]],
  ['Outlook / Hotmail', [
    'outlook.com → roue dentée → « Courrier » → « Transfert ».',
    'Activez le transfert vers l’adresse dédiée et cochez « Conserver une copie des messages transférés ».',
  ]],
  ['Autre (Orange, Free…)', [
    'Dans les réglages de votre messagerie, cherchez « Transfert » ou « Redirection », et indiquez l’adresse dédiée en gardant une copie.',
  ]],
];

function domaineDe(adresse: string): string {
  const d = adresse.trim().toLowerCase().split('@')[1] ?? '';
  return /^[a-z0-9.-]+\.[a-z]{2,}$/.test(d) ? d : '';
}

function BlocMails({ faire, presentes }: { faire: (f: () => Promise<unknown>, succes: string) => Promise<boolean>; presentes: boolean }) {
  const [boite, setBoite] = useState({ adresse: '', motDePasse: '', hote: '' });
  const [fournisseur, setFournisseur] = useState<FournisseurMail | null | undefined>(undefined);
  const [derniers, setDerniers] = useState<MailReleve[]>([]);
  const domaine = domaineDe(boite.adresse);

  useEffect(() => {
    if (!domaine) {
      setFournisseur(undefined);
      return;
    }
    let actif = true;
    const t = window.setTimeout(() => void api.fournisseurMail(domaine).then((r) => actif && setFournisseur(r.fournisseur)).catch(() => undefined), 300);
    return () => {
      actif = false;
      window.clearTimeout(t);
    };
  }, [domaine]);

  const chargerDerniers = useCallback(() => void api.mails(5).then(setDerniers).catch(() => undefined), []);
  useEffect(() => {
    if (presentes) chargerDerniers();
  }, [presentes, chargerDerniers]);

  const ajouter = async (e: FormEvent) => {
    e.preventDefault();
    const corps = { adresse: boite.adresse.trim(), motDePasse: boite.motDePasse, ...(fournisseur === null && boite.hote.trim() ? { hote: boite.hote.trim() } : {}) };
    if (await faire(() => api.connecterBoite(corps), 'Boîte branchée : les mails arrivent.')) {
      setBoite({ adresse: '', motDePasse: '', hote: '' });
      chargerDerniers();
    }
  };

  return (
    <Fenetre>
      <span className="display" style={{ fontWeight: 500 }}>Mails</span>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        Le plus prudent : Alfred lit une boîte qui lui est dédiée, où votre boîte principale transfère une copie de vos mails.
        Il n’a jamais le mot de passe de votre vraie boîte, et vous coupez tout en arrêtant le transfert.
      </p>
      <details>
        <summary className="muted" style={{ fontSize: 13 }}>Créer la boîte dédiée et le transfert</summary>
        <ol className="muted" style={{ fontSize: 13, paddingLeft: 18 }}>
          <li>Créez une adresse pour Alfred seul, par exemple chez GMX (gmx.fr, le plus simple) ou Gmail.</li>
          <li>GMX : activez l’accès IMAP (Paramètres → POP3 &amp; IMAP). Gmail : activez la validation en deux étapes, puis créez un mot de passe d’application (myaccount.google.com/apppasswords).</li>
          <li>Dans votre boîte principale, activez le transfert automatique vers cette adresse (ci-dessous).</li>
          <li>Branchez ici la boîte dédiée. Seuls les mails reçus après le transfert arriveront.</li>
        </ol>
        {TRANSFERTS.map(([nom, etapes]) => (
          <details key={nom} style={{ marginLeft: 12 }}>
            <summary className="muted" style={{ fontSize: 13 }}>Transfert depuis {nom}</summary>
            <ol className="muted" style={{ fontSize: 13, paddingLeft: 18 }}>{etapes.map((x) => <li key={x}>{x}</li>)}</ol>
          </details>
        ))}
        <p className="muted" style={{ fontSize: 12 }}>Les mails transférés tombent parfois dans les indésirables de la boîte dédiée : Alfred lit aussi ce dossier.</p>
      </details>
      <form className="formulaire" onSubmit={(e) => void ajouter(e)}>
        <input className="saisie texte" type="email" placeholder="Adresse de la boîte à lire" autoComplete="off" value={boite.adresse} onChange={(e) => setBoite({ ...boite, adresse: e.target.value })} />
        {fournisseur && <small className={fournisseur.relevable ? 'muted' : 'attention'}>{fournisseur.nom} · {fournisseur.aide}</small>}
        {fournisseur === null && (
          <input className="saisie texte" placeholder="Serveur IMAP (imap.exemple.fr)" autoComplete="off" value={boite.hote} onChange={(e) => setBoite({ ...boite, hote: e.target.value })} />
        )}
        {fournisseur?.relevable !== false && (
          <input className="saisie texte" type="password" placeholder={fournisseur?.motDePasseApplication ? 'Mot de passe d’application' : 'Mot de passe'} autoComplete="new-password" value={boite.motDePasse} onChange={(e) => setBoite({ ...boite, motDePasse: e.target.value })} />
        )}
        <button className="btn principal" disabled={!domaine || !boite.motDePasse || fournisseur?.relevable === false}>Tester et brancher</button>
      </form>
      {derniers.length > 0 && (
        <div className="derniers-mails">
          <small className="muted">Derniers mails relevés · <a href="#/mails">tous les mails</a></small>
          {derniers.map((m) => (
            <div key={`${m.recuLe}:${m.sujet}`} className="t">
              <span>{m.sujet || '(sans objet)'}</span>
              <small className="muted">{m.de} · {new Date(m.recuLe).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}{m.indesirable ? ' · indésirables' : ''}</small>
            </div>
          ))}
        </div>
      )}
    </Fenetre>
  );
}

/** Où trouver l'adresse secrète d'un agenda, selon le service. */
const ADRESSES_AGENDA: [string, string[]][] = [
  ['Google Agenda', [
    'Sur ordinateur, calendar.google.com → roue dentée → « Paramètres ».',
    'À gauche, cliquez sur l’agenda voulu, puis descendez jusqu’à « Intégrer l’agenda ».',
    'Copiez l’« Adresse secrète au format iCal » (elle finit par basic.ics). Ne la partagez avec personne d’autre.',
  ]],
  ['Outlook', [
    'outlook.com → roue dentée → « Calendrier » → « Calendriers partagés ».',
    'Sous « Publier un calendrier », choisissez l’agenda et « Peut afficher tous les détails », puis « Publier ».',
    'Copiez le lien ICS.',
  ]],
  ['Autre (école, club, Proton…)', [
    'Cherchez « exporter », « s’abonner » ou « lien iCal » : toute adresse qui finit par .ics, ou qui commence par webcal://, convient.',
  ]],
];

function BlocIcs({ faire }: { faire: (f: () => Promise<unknown>, succes: string) => Promise<boolean> }) {
  const [agenda, setAgenda] = useState({ nom: '', url: '' });
  const ajouter = async (e: FormEvent) => {
    e.preventDefault();
    if (await faire(() => api.connecterIcs(agenda.url.trim(), agenda.nom.trim()), 'Agenda ajouté.')) setAgenda({ nom: '', url: '' });
  };
  return (
    <Fenetre>
      <span className="display" style={{ fontWeight: 500 }}>Autres agendas (Google, Outlook…)</span>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        Par l’adresse secrète de l’agenda, en lecture seule. Ce qui y est écrit est traité comme externe, sauf si vous le déclarez de confiance une fois ajouté.
      </p>
      <details>
        <summary className="muted" style={{ fontSize: 13 }}>Où trouver l’adresse de l’agenda ?</summary>
        {ADRESSES_AGENDA.map(([nom, etapes]) => (
          <details key={nom} style={{ marginLeft: 12 }}>
            <summary className="muted" style={{ fontSize: 13 }}>{nom}</summary>
            <ol className="muted" style={{ fontSize: 13, paddingLeft: 18 }}>{etapes.map((x) => <li key={x}>{x}</li>)}</ol>
          </details>
        ))}
      </details>
      <form className="formulaire" onSubmit={(e) => void ajouter(e)}>
        <input className="saisie texte" placeholder="Nom (« Agenda de Camille »)" maxLength={60} value={agenda.nom} onChange={(e) => setAgenda({ ...agenda, nom: e.target.value })} />
        <input className="saisie texte" type="url" placeholder="https://… ou webcal://…" autoComplete="off" value={agenda.url} onChange={(e) => setAgenda({ ...agenda, url: e.target.value })} />
        <button className="btn principal" disabled={!/^(https|webcals?):\/\//i.test(agenda.url.trim())}>Tester et ajouter</button>
      </form>
    </Fenetre>
  );
}

/** Choisir un dossier du dossier des documents (sur le Pi, le partage du NAS) et l'indexer. Absent en cloud. */
function BlocDocuments({ faire }: { faire: (f: () => Promise<unknown>, succes: string) => Promise<boolean> }) {
  const [chemin, setChemin] = useState('');
  const [liste, setListe] = useState<{ disponible: boolean; dossiers: string[] } | null>(null);
  const [nom, setNom] = useState('');

  useEffect(() => {
    void api.dossiersDocuments(chemin).then(setListe).catch(() => setListe({ disponible: true, dossiers: [] }));
  }, [chemin]);

  if (!liste?.disponible) return null;
  const parent = chemin.includes('/') ? chemin.slice(0, chemin.lastIndexOf('/')) : '';
  const ajouter = async (e: FormEvent) => {
    e.preventDefault();
    if (await faire(() => api.indexerDossier(chemin, nom.trim() || chemin.split('/').pop() || 'Documents'), 'Dossier indexé : le texte sera lu en arrière-plan.')) setNom('');
  };
  return (
    <Fenetre>
      <span className="display" style={{ fontWeight: 500 }}>Documents</span>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        Alfred indexe un dossier du NAS (le nom, la date et le texte des PDF et des fichiers Word) pour retrouver un document
        à la demande. Il ne modifie jamais rien : le dossier est en lecture seule. <a href="#/documents">Chercher dans les documents</a>
      </p>
      <div className="choix-dossier">
        <small className="muted">Dossier : /{chemin}</small>
        {chemin && <button type="button" className="btn petit" onClick={() => setChemin(parent)}>↑ Remonter</button>}
        {liste.dossiers.map((d) => (
          <button key={d} type="button" className="btn petit" onClick={() => setChemin(chemin ? `${chemin}/${d}` : d)}>{d}</button>
        ))}
      </div>
      <form className="formulaire" onSubmit={(e) => void ajouter(e)}>
        <input className="saisie texte" placeholder={`Nom (« ${chemin.split('/').pop() || 'Documents'} »)`} maxLength={60} value={nom} onChange={(e) => setNom(e.target.value)} />
        <button className="btn principal">Indexer /{chemin}</button>
      </form>
    </Fenetre>
  );
}

export function Connexions({ actions }: { actions: Actions }) {
  const [connexions, setConnexions] = useState<Connexion[] | null>(null);
  const [confiance, setConfiance] = useState(false);
  const [icloud, setIcloud] = useState({ utilisateur: '', motDePasse: '' });

  const echec = useCallback((e: unknown) => {
    if (e instanceof NonConnecte) window.location.hash = '#/connexion';
    else actions.toast({ texte: (e as Error).message, erreur: true });
  }, [actions]);

  const charger = useCallback(() => {
    api.connexions().then(setConnexions).catch(echec);
    api.agendaConfiance().then((r) => setConfiance(r.confiance)).catch(() => undefined);
  }, [echec]);

  useEffect(charger, [charger]);

  const faire = async (f: () => Promise<unknown>, succes: string): Promise<boolean> => {
    try {
      await f();
      actions.toast({ texte: succes });
      charger();
      return true;
    } catch (e) {
      echec(e);
      return false;
    }
  };

  const ajouterIcloud = async (e: FormEvent) => {
    e.preventDefault();
    if (await faire(() => api.connecterIcloud(icloud.utilisateur.trim(), icloud.motDePasse.trim()), 'Agenda iCloud connecté.')) setIcloud({ utilisateur: '', motDePasse: '' });
  };

  const icloudPresent = connexions?.some((c) => c.fournisseur === 'icloud') ?? false;

  return (
    <main className="ecran">
      <EnTeteRetour titre="Connexions" retour="#/reglages" />

      <Fenetre>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Ce qu’Alfred lit chez d’autres services, en lecture seule. Il ne modifie jamais rien chez eux.
        </p>
        {connexions === null && <p className="muted" style={{ margin: 0 }}>Chargement…</p>}
        {connexions?.length === 0 && <p className="muted" style={{ margin: 0 }}>Aucune connexion pour l’instant.</p>}
        {connexions?.map((c) => <Ligne key={`${c.id}:${c.etat}`} c={c} faire={faire} />)}
      </Fenetre>

      <BlocDocuments faire={faire} />

      <BlocIcs faire={faire} />

      <BlocMails faire={faire} presentes={connexions?.some((c) => c.type === 'mail') ?? false} />

      <Fenetre>
        <span className="display" style={{ fontWeight: 500 }}>Agenda iCloud</span>
        {icloudPresent && (
          <label className="entretien-case">
            <input type="checkbox" checked={confiance} onChange={(e) => void api.reglerAgendaConfiance(e.target.checked).then((r) => { setConfiance(r.confiance); actions.toast({ texte: r.confiance ? 'Agenda de confiance : Alfred agit sans validation.' : 'Agenda partagé : les écritures demanderont votre validation.' }); }).catch(echec)} />
            <span>Agenda de confiance<br /><small className="muted">Cochez si vous seul y écrivez. Sinon, ce qu’un autre y met est traité comme externe : ce qu’Alfred voudra écrire après l’avoir lu attendra votre validation.</small></span>
          </label>
        )}
        <details>
          <summary className="muted" style={{ fontSize: 13 }}>Comment obtenir un mot de passe d’application ?</summary>
          <ol className="muted" style={{ fontSize: 13, paddingLeft: 18 }}>
            <li>Ouvrez appleid.apple.com et connectez-vous.</li>
            <li>Connexion et sécurité → Mots de passe d’app → « + ».</li>
            <li>Nommez-le « Alfred » et recopiez le mot de passe affiché (xxxx-xxxx-xxxx-xxxx).</li>
          </ol>
          <p className="muted" style={{ fontSize: 12 }}>Alfred ne fait que lire votre agenda. Ce mot de passe peut être révoqué à tout moment chez Apple.</p>
        </details>
        <form className="formulaire" onSubmit={(e) => void ajouterIcloud(e)}>
          <input className="saisie texte" type="email" placeholder="Identifiant Apple" value={icloud.utilisateur} onChange={(e) => setIcloud({ ...icloud, utilisateur: e.target.value })} />
          <input className="saisie texte" placeholder="xxxx-xxxx-xxxx-xxxx" autoComplete="off" value={icloud.motDePasse} onChange={(e) => setIcloud({ ...icloud, motDePasse: e.target.value })} />
          <button className="btn principal">{icloudPresent ? 'Ajouter un autre compte' : 'Connecter'}</button>
        </form>
      </Fenetre>
    </main>
  );
}
